import React, { useState, useEffect } from 'react';
import { X, Mail, Phone, MapPin, Calendar, Shield, ShieldOff, Key, UserCog, Edit, UserRound } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Separator } from '../ui/separator';
import { User } from '../types/user';
import { motion, AnimatePresence } from 'framer-motion';
import { updateUserDetails, changeUserRole, changeUserPassword, updateUserBlockStatus } from '../../services/usersapi';
import { toast } from 'react-hot-toast';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';

interface UserDetailSidebarProps {
  user: User | null;
  onClose: () => void;
  onUserUpdate?: (updatedUser: User) => void;
}

interface EditUserData {
  username: string;
  location: string;
  phone: string;
}

const sidebarVariants = {
  hidden: { x: '100%', opacity: 0 },
  visible: { x: 0, opacity: 1 },
  exit: { x: '100%', opacity: 0 },
};

const UserDetailSidebar: React.FC<UserDetailSidebarProps> = ({ user, onClose, onUserUpdate }) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editData, setEditData] = useState<EditUserData>({
    username: user?.name || '',
    location: user?.profile?.college?.district || '',
    phone: user?.mobile || '',
  });
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(user?.role || '');
  const [selectedManagerUserId, setSelectedManagerUserId] = useState('');
  const [roleChangeLoading, setRoleChangeLoading] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);

  const managerOptions = [
    { id: 'manager1', username: 'Manager One', email: 'manager1@example.com' },
    { id: 'manager2', username: 'Manager Two', email: 'manager2@example.com' },
  ];

  useEffect(() => {
    if (user) {
      setEditData({
        username: user.name,
        location: user.profile?.college?.district || '',
        phone: user.mobile || '',
      });
      setSelectedRole(user.role || '');
    }
  }, [user]);

  if (!user) return null;

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'HR': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'SHM': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'BDE': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusBadgeColor = (status: boolean) => {
    return status 
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-red-100 text-red-800 border-red-200';
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async () => {
    try {
      const response = await updateUserDetails(user.id, editData);
      toast.success('User details updated successfully');
      setIsEditOpen(false);
      if (onUserUpdate && response.data.user) {
        onUserUpdate(response.data.user);
      }
    } catch (error) {
      console.error('Failed to update user details:', error);
      toast.error('Failed to update user details');
    }
  };

  const handleRoleChange = async () => {
    if (!user || !selectedRole || !selectedManagerUserId) {
      toast.error('Please select a role and a manager');
      return;
    }
    setRoleChangeLoading(true);
    try {
      await changeUserRole(user.id, {
        newRole: selectedRole === 'BDE' ? 'FBDE' : selectedRole,
        newManagerUserId: selectedManagerUserId,
      });
      toast.success(`Role changed to ${selectedRole} for ${user.name}`);
      setIsRoleDialogOpen(false);
      if (onUserUpdate) {
        onUserUpdate({ ...user, role: selectedRole === 'BDE' ? 'FBDE' : selectedRole });
      }
    } catch (error) {
      console.error('Failed to change user role:', error);
      toast.error('Failed to change role');
    }
    setRoleChangeLoading(false);
    setSelectedRole('');
    setSelectedManagerUserId('');
  };

  const handlePasswordChange = async () => {
    if (!user || !newPassword || !confirmPassword) {
      toast.error('Please fill in both password fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setPasswordChangeLoading(true);
    try {
      await changeUserPassword(user.id, newPassword, confirmPassword);
      toast.success('Password updated successfully');
      setIsPasswordDialogOpen(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Failed to update password');
    }
    setPasswordChangeLoading(false);
  };

  const handleBlockToggle = async () => {
    if (!user) return;
    setBlockLoading(true);
    try {
      await updateUserBlockStatus(user.id, !user.isActive);
      toast.success(user.isActive ? 'User blocked' : 'User unblocked');
      if (onUserUpdate) {
        onUserUpdate({ ...user, isActive: !user.isActive });
      }
    } catch (error) {
      console.error('Failed to update user status:', error);
      toast.error('Failed to update user status');
    }
    setBlockLoading(false);
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={user.id}
        variants={sidebarVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="fixed top-0 right-0 h-full w-96 bg-white border-l border-border z-50 shadow-xl"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">User Details</h2>
            <Button variant="ghost" size="sm" onClick={onClose} className="w-8 h-8 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* User Profile */}
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16">
                <AvatarFallback className="text-lg">
                  {user.name?.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="text-xl font-semibold text-foreground">{user.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={getRoleBadgeColor(user.role)}>{user.role}</Badge>
                  <Badge className={getStatusBadgeColor(user.isActive)}>
                    {user.isActive ? 'Active' : 'Blocked'}
                  </Badge>
                </div>
              </div>
            </div>

            <Separator />

            {/* Contact Information */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3 flex items-center justify-between">
                Contact Information
                <Button variant="ghost" size="sm" onClick={() => setIsEditOpen(true)}>
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              </h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{user.email}</p>
                    <p className="text-xs text-muted-foreground">Email Address</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{user.mobile}</p>
                    <p className="text-xs text-muted-foreground">Phone Number</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{user.profile?.college?.district || 'Not specified'}</p>
                    <p className="text-xs text-muted-foreground">Location</p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Personal Information */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Personal Information</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{user.profile?.dateOfBirth ? new Date(user.profile?.dateOfBirth).toLocaleDateString() : 'Not specified'}</p>
                    <p className="text-xs text-muted-foreground">Date of Birth</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                <UserRound className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{user.profile?.gender || 'Not specified'}</p>
                    <p className="text-xs text-muted-foreground">Gender</p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Education Information */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Education Information</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm text-foreground">{user.profile?.college?.college || 'Not specified'}</p>
                    <p className="text-xs text-muted-foreground">College</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm text-foreground">{user.profile?.college?.university || 'Not specified'}</p>
                    <p className="text-xs text-muted-foreground">University</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm text-foreground">{user.profile?.college?.college_type || 'Not specified'}</p>
                    <p className="text-xs text-muted-foreground">College Type</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm text-foreground">{user.profile?.college?.state || 'Not specified'}</p>
                    <p className="text-xs text-muted-foreground">State</p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Professional Information */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Professional Information</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm text-foreground">{user.profile?.bio || 'Not specified'}</p>
                    <p className="text-xs text-muted-foreground">Bio</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm text-foreground">{user.preferredJobRole || 'Not specified'}</p>
                    <p className="text-xs text-muted-foreground">Preferred Job Role</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm text-foreground">{user.profile?.professionalInformation?.workMode || 'Not specified'}</p>
                    <p className="text-xs text-muted-foreground">Work Mode</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{new Date(user.createdAt).toLocaleDateString()}</p>
                    <p className="text-xs text-muted-foreground">Join Date</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-foreground">{user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Not specified'}</p>
                    <p className="text-xs text-muted-foreground">Last Login</p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Verification Status */}
            <div className="pb-6">
              <h4 className="text-sm font-medium text-foreground mb-3">Verification Status</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm text-foreground">{user.isEmailVerified ? 'Verified' : 'Not Verified'}</p>
                    <p className="text-xs text-muted-foreground">Email Verification</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm text-foreground">{user.isPhoneVerified ? 'Verified' : 'Not Verified'}</p>
                    <p className="text-xs text-muted-foreground">Phone Verification</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          {/* <div className="p-6 border-t border-border space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsRoleDialogOpen(true)}>
                <UserCog className="w-4 h-4 mr-2" />
                Change Role
              </Button>
              <Button variant="outline" size="sm" onClick={() => setIsPasswordDialogOpen(true)}>
                <Key className="w-4 h-4 mr-2" />
                Reset Password
              </Button>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant={user.isActive ? 'destructive' : 'default'}
                  className="w-full"
                  size="sm"
                  disabled={blockLoading}
                >
                  {blockLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Processing...
                    </span>
                  ) : user.isActive ? (
                    <>
                      <ShieldOff className="w-4 h-4 mr-2" />
                      Block User
                    </>
                  ) : (
                    <>
                      <Shield className="w-4 h-4 mr-2" />
                      Unblock User
                    </>
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{user.isActive ? 'Block User' : 'Unblock User'}</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to {user.isActive ? 'block' : 'unblock'}{' '}
                    <span className="font-semibold text-[#EC6E51]">{user.name}</span>?{' '}
                    {user.isActive
                      ? 'This will prevent them from accessing the system.'
                      : 'This will restore their access to the system.'}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleBlockToggle}>
                    {user.isActive ? 'Block' : 'Unblock'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div> */}

          {/* Change Role Dialog */}
          <AnimatePresence>
            {isRoleDialogOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
                onClick={() => setIsRoleDialogOpen(false)}
              >
                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 50, opacity: 0 }}
                  className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md"
                  onClick={e => e.stopPropagation()}
                >
                  <h3 className="text-lg font-semibold mb-4">
                    Change Role for <span className="text-[#EC6E51]">{user.name}</span>
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground">Role</label>
                      <select
                        className="w-full p-2 border border-border rounded-md"
                        value={selectedRole}
                        onChange={e => setSelectedRole(e.target.value)}
                      >
                        <option value="">Select Role</option>
                        <option value="HR">HR</option>
                        <option value="SHM">SHM</option>
                        <option value="BDE">BDE</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">Manager</label>
                      <select
                        className="w-full p-2 border border-border rounded-md"
                        value={selectedManagerUserId}
                        onChange={e => setSelectedManagerUserId(e.target.value)}
                      >
                        <option value="">Select Manager</option>
                        {managerOptions.map(m => (
                          <option key={m.id} value={m.id}>
                            {m.username} ({m.email})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-2">
                    < Button variant="outline" onClick={() => setIsRoleDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleRoleChange} disabled={roleChangeLoading}>
                      {roleChangeLoading ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Change Password Dialog */}
          <AnimatePresence>
            {isPasswordDialogOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
                onClick={() => setIsPasswordDialogOpen(false)}
              >
                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 50, opacity: 0 }}
                  className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md"
                  onClick={e => e.stopPropagation()}
                >
                  <h3 className="text-lg font-semibold mb-4">
                    Change Password for <span className="text-[#EC6E51]">{user.name}</span>
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground">New Password</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        className="w-full p-2 border border-border rounded-md"
                        placeholder="Enter new password"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">Confirm Password</label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        className="w-full p-2 border border-border rounded-md"
                        placeholder="Confirm new password"
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsPasswordDialogOpen(false);
                        setNewPassword('');
                        setConfirmPassword('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handlePasswordChange} disabled={passwordChangeLoading}>
                      {passwordChangeLoading ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Edit Popup */}
          <AnimatePresence>
            {isEditOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
                onClick={() => setIsEditOpen(false)}
              >
                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 50, opacity: 0 }}
                  className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md"
                  onClick={e => e.stopPropagation()}
                >
                  <h3 className="text-lg font-semibold mb-4">
                    Edit User Details for <span className="text-[#EC6E51]">{user.name}</span>
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground">Username</label>
                      <input
                        type="text"
                        name="username"
                        value={editData.username}
                        onChange={handleEditChange}
                        className="w-full p-2 border border-border rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">Location</label>
                      <input
                        type="text"
                        name="location"
                        value={editData.location}
                        onChange={handleEditChange}
                        className="w-full p-2 border border-border rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground">Phone</label>
                      <input
                        type="text"
                        name="phone"
                        value={editData.phone}
                        onChange={handleEditChange}
                        className="w-full p-2 border border-border rounded-md"
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsEditOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveEdit}>Save</Button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UserDetailSidebar;